"use client";

import { ListTodoIcon } from "lucide-react";
import * as React from "react";

import { cn } from "../../cn";
import { AvatarStack, AvatarTile } from "../avatar-tile";
import { Checkbox } from "../checkbox";
import { EmptyState } from "../empty-state";
import { FOCUS_RING, FOCUS_RING_INSET } from "../focus-ring";
import { GroupHeader, MetaCounts } from "../list-row";
import { HOVER_EASE } from "../states";
import { InlineAdd } from "./inline-add";
import { useRowWindow, WINDOW_AFTER } from "./row-window";
import {
  countItems,
  DueLabel,
  PriorityMark,
  StatusBadge,
  StatusGlyph,
  TaskLabel,
  type TaskSummary,
  toAvatar,
} from "./task-meta";

// The list view (Work OS container-view ListView, design spec §6.3 rows): groups with a sticky
// 32 px disclosure header and a count, 36 px rows (44 on coarse pointers) with a selection
// checkbox (revealed on hover/focus or once anything is selected), a done toggle, the title,
// chips and counts, then fixed columns for assignees, due date and priority. The caller owns
// selection, completion and grouping; the view owns only which groups are folded.

export type ListViewGroup = {
  id: string;
  title: React.ReactNode;
  /** Plain-text name for labels ("Add task to …"). */
  name?: string;
  tasks: TaskSummary[];
  /** Controls on the group's header (rename, delete), shown on hover or focus. */
  actions?: React.ReactNode;
};

export type ListViewColumn = "assignees" | "due" | "priority";

/** Render slots for cells the app edits in the row (a status menu, an assignee menu). */
export type ListViewCells = {
  status?: (task: TaskSummary) => React.ReactNode;
  assignees?: (task: TaskSummary) => React.ReactNode;
  /** WORK-OS: inline due-date and priority editors (My tasks). */
  due?: (task: TaskSummary) => React.ReactNode;
  priority?: (task: TaskSummary) => React.ReactNode;
  /** WOS-BUNDLE: the row's trailing controls (a timer, a menu), revealed on hover or focus. */
  actions?: (task: TaskSummary) => React.ReactNode;
};

export type ListViewProps = Omit<React.ComponentProps<"div">, "children"> & {
  groups: ListViewGroup[];
  columns?: ListViewColumn[];
  /** Inline editors in place of the status badge and the assignee column. */
  cells?: ListViewCells;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  /** The task open in the drawer. */
  activeTaskId?: string;
  onTaskOpen?: (taskId: string) => void;
  onToggleDone?: (taskId: string, done: boolean) => void;
  onAddTask?: (groupId: string, title: string) => void;
  /**
   * WOS-BUNDLE: rows drag between groups and within one; `toIndex` is the place in the
   * destination group once the row has left its origin (as `BoardView`'s `onTaskMove`).
   */
  onTaskDrop?: (taskId: string, toGroupId: string, toIndex: number) => void;
  /** The header row with column captions (on by default). */
  header?: boolean;
  empty?: React.ReactNode;
};

const ALL_COLUMNS: ListViewColumn[] = ["assignees", "due", "priority"];
/** The trailing actions' width (three 24 px icon buttons), so the header's captions line up. */
const ACTIONS_W = "w-20 shrink-0";
// Assignees fit one tile and a name (Linear's shape); priority is wide enough for its caption.
const COLUMN_W: Record<ListViewColumn, string> = {
  assignees: "w-36",
  due: "w-20",
  priority: "w-14",
};
const COLUMN_LABEL: Record<ListViewColumn, string> = {
  assignees: "Assignees",
  due: "Due",
  priority: "Priority",
};

export function ListView({
  groups,
  columns = ALL_COLUMNS,
  cells,
  selectedIds,
  onSelectionChange,
  activeTaskId,
  onTaskOpen,
  onToggleDone,
  onAddTask,
  onTaskDrop,
  header = true,
  empty,
  className,
  "aria-label": ariaLabel = "Tasks",
  ...props
}: ListViewProps) {
  const [folded, setFolded] = React.useState<string[]>([]);
  const [dragId, setDragId] = React.useState<string | null>(null);
  const [target, setTarget] = React.useState<{ groupId: string; index: number } | null>(null);
  const endDrag = () => {
    setDragId(null);
    setTarget(null);
  };
  /** Where the pointer would drop in `group`: before the first row whose middle is below it. */
  const indexAt = (group: ListViewGroup, el: HTMLElement, y: number) => {
    for (const row of el.querySelectorAll<HTMLElement>("li[data-task-id]")) {
      const r = row.getBoundingClientRect();
      if (y < r.top + r.height / 2) {
        const i = group.tasks.findIndex((t) => t.id === row.dataset.taskId);
        if (i >= 0) return i;
      }
    }
    return group.tasks.length;
  };
  const drop = (group: ListViewGroup) => {
    const id = dragId;
    const at = target?.groupId === group.id ? target.index : group.tasks.length;
    endDrag();
    if (!id || !onTaskDrop) return;
    const from = group.tasks.findIndex((t) => t.id === id);
    const to = from >= 0 && from < at ? at - 1 : at;
    if (from === to) return;
    onTaskDrop(id, group.id, to);
  };
  const selected = selectedIds ?? [];
  const selecting = selected.length > 0;
  const total = groups.reduce((n, g) => n + g.tasks.length, 0);

  const toggleSelect = (id: string, on: boolean) =>
    onSelectionChange?.(on ? [...selected, id] : selected.filter((x) => x !== id));

  if (total === 0 && !onAddTask)
    return (
      <div data-slot="list-view" className={className}>
        {empty ?? <EmptyState icon={ListTodoIcon} title="No tasks here yet" variant="card" />}
      </div>
    );

  return (
    <section
      data-slot="list-view"
      aria-label={ariaLabel}
      className={cn("flex min-w-0 flex-col", className)}
      {...props}
    >
      {header ? (
        <div
          aria-hidden={true}
          className="sticky top-0 z-sticky flex h-8 items-center gap-2.5 border-b border-border bg-background px-3 text-xs font-medium text-muted-foreground"
        >
          {onSelectionChange ? <span className="w-4" /> : null}
          <span className="w-4" />
          <span className="flex-1">Task</span>
          {columns.map((c) => (
            <span key={c} className={cn(COLUMN_W[c], "shrink-0 max-sm:hidden")}>
              {COLUMN_LABEL[c]}
            </span>
          ))}
          {cells?.actions ? <span className={ACTIONS_W} /> : null}
        </div>
      ) : null}
      {groups.map((group) => {
        const open = !folded.includes(group.id);
        const name = group.name ?? (typeof group.title === "string" ? group.title : "group");
        return (
          // biome-ignore lint/a11y/noStaticElementInteractions: the drop zone of a pointer drag; the row's "Move to…" menu is its keyboard twin.
          <div
            key={group.id}
            data-slot="list-view-group"
            data-group-id={group.id}
            data-drop-target={(dragId && target?.groupId === group.id) || undefined}
            {...(onTaskDrop
              ? {
                  onDragOver: (e: React.DragEvent<HTMLDivElement>) => {
                    if (!dragId) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    const index = indexAt(group, e.currentTarget, e.clientY);
                    if (target?.groupId !== group.id || target.index !== index)
                      setTarget({ groupId: group.id, index });
                  },
                  onDrop: (e: React.DragEvent<HTMLDivElement>) => {
                    e.preventDefault();
                    drop(group);
                  },
                }
              : {})}
          >
            <GroupHeader
              label={group.title}
              count={group.tasks.length}
              actions={group.actions}
              open={open}
              onOpenChange={(v) =>
                setFolded((f) => (v ? f.filter((id) => id !== group.id) : [...f, group.id]))
              }
              className={header ? "top-8" : undefined}
            />
            {open ? (
              <GroupRows
                tasks={group.tasks}
                windowed={total > WINDOW_AFTER}
                row={(task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    columns={columns}
                    cells={cells}
                    selected={selected.includes(task.id)}
                    selecting={selecting}
                    active={task.id === activeTaskId}
                    onSelect={onSelectionChange ? toggleSelect : undefined}
                    onOpen={onTaskOpen}
                    onToggleDone={onToggleDone}
                    {...(onTaskDrop
                      ? {
                          drag: {
                            dragging: dragId === task.id,
                            dropBefore:
                              dragId !== null &&
                              target?.groupId === group.id &&
                              group.tasks[target.index]?.id === task.id,
                            onStart: () => setDragId(task.id),
                            onEnd: endDrag,
                          },
                        }
                      : {})}
                  />
                )}
              >
                {dragId && target?.groupId === group.id && target.index >= group.tasks.length ? (
                  <li aria-hidden={true} data-slot="drop-line" className="h-0.5 bg-primary" />
                ) : null}
                {onAddTask ? (
                  <li className="border-b border-border-subtle pl-9">
                    <InlineAdd
                      fieldLabel={`New task in ${name}`}
                      onAdd={(title) => onAddTask(group.id, title)}
                    />
                  </li>
                ) : null}
              </GroupRows>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}

/** A group's rows; in a long list (WORK-PERF) only those near the viewport are mounted. */
function GroupRows({
  tasks,
  windowed,
  row,
  children,
}: {
  tasks: TaskSummary[];
  windowed: boolean;
  row: (task: TaskSummary) => React.ReactNode;
  children?: React.ReactNode;
}) {
  const ref = React.useRef<HTMLUListElement>(null);
  const w = useRowWindow(ref, tasks.length, { enabled: windowed });
  return (
    <ul ref={ref} className="m-0 list-none p-0" data-windowed={windowed || undefined}>
      {w.before > 0 ? <li aria-hidden={true} style={{ height: w.before }} /> : null}
      {tasks.slice(w.start, w.end).map(row)}
      {w.after > 0 ? <li aria-hidden={true} style={{ height: w.after }} /> : null}
      {children}
    </ul>
  );
}

function TaskRow({
  task,
  columns,
  cells,
  selected,
  selecting,
  active,
  onSelect,
  onOpen,
  onToggleDone,
  drag,
}: {
  task: TaskSummary;
  columns: ListViewColumn[];
  cells?: ListViewCells | undefined;
  selected: boolean;
  selecting: boolean;
  active: boolean;
  onSelect?: ((id: string, on: boolean) => void) | undefined;
  onOpen?: ((id: string) => void) | undefined;
  onToggleDone?: ((id: string, done: boolean) => void) | undefined;
  drag?: { dragging: boolean; dropBefore: boolean; onStart: () => void; onEnd: () => void };
}) {
  const counts = countItems(task);
  const titleCls = cn(
    "min-w-0 truncate text-left text-base text-foreground outline-none",
    task.done && "text-muted-foreground line-through",
  );
  return (
    <li
      data-slot="list-view-row"
      data-list-row=""
      data-window-row=""
      data-task-id={task.id}
      data-selected={selected || active || undefined}
      data-dragging={drag?.dragging || undefined}
      {...(drag
        ? {
            draggable: true,
            onDragStart: (e: React.DragEvent<HTMLLIElement>) => {
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", task.id);
              drag.onStart();
            },
            onDragEnd: drag.onEnd,
          }
        : {})}
      className={cn(
        drag?.dropBefore &&
          "before:absolute before:inset-x-0 before:-top-px before:z-20 before:h-0.5 before:bg-primary before:content-['']",
        drag?.dragging && "opacity-50",
        "group/row relative flex h-9 min-w-0 items-center gap-2.5 border-b border-subtle bg-card px-3 pointer-coarse:h-11 hover:bg-sunken data-selected:bg-primary-soft",
        "has-[[data-row-title]:focus-visible]:outline-2 has-[[data-row-title]:focus-visible]:-outline-offset-2 has-[[data-row-title]:focus-visible]:outline-ring has-[[data-row-title]:focus-visible]:outline-solid",
        HOVER_EASE,
      )}
    >
      {onSelect ? (
        <Checkbox
          checked={selected}
          onCheckedChange={(v) => onSelect(task.id, Boolean(v))}
          aria-label={`Select ${task.title}`}
          className={cn(
            "relative z-10 opacity-0 group-hover/row:opacity-100 group-focus-within/row:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100",
            (selecting || selected) && "opacity-100",
          )}
        />
      ) : null}
      {onToggleDone ? (
        <button
          type="button"
          aria-pressed={task.done ?? false}
          aria-label={task.done ? `Reopen ${task.title}` : `Complete ${task.title}`}
          onClick={() => onToggleDone(task.id, !task.done)}
          className={cn(
            "relative z-10 flex size-4 shrink-0 items-center justify-center rounded-full hover:text-success",
            FOCUS_RING,
          )}
        >
          <StatusGlyph task={task} />
        </button>
      ) : (
        <span className="flex size-4 shrink-0 items-center justify-center">
          <StatusGlyph task={task} />
        </span>
      )}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {onOpen || task.href ? (
          task.href && !onOpen ? (
            <a
              data-row-title=""
              data-row-focus=""
              href={task.href}
              aria-current={active ? "true" : undefined}
              className={cn(titleCls, "after:absolute after:inset-0 after:content-['']")}
            >
              {task.title}
            </a>
          ) : (
            <button
              data-row-title=""
              data-row-focus=""
              type="button"
              aria-current={active ? "true" : undefined}
              onClick={() => onOpen?.(task.id)}
              className={cn(
                titleCls,
                "cursor-pointer after:absolute after:inset-0 after:content-['']",
                FOCUS_RING_INSET,
              )}
            >
              {task.title}
            </button>
          )
        ) : (
          <span className={titleCls}>{task.title}</span>
        )}
        {task.location ? (
          <span className="min-w-0 max-w-[220px] shrink truncate text-xs text-muted-foreground max-lg:hidden">
            {task.location}
          </span>
        ) : null}
        <span className="relative z-10 flex shrink-0 items-center gap-1 max-sm:hidden">
          {cells?.status ? (
            cells.status(task)
          ) : task.status ? (
            <StatusBadge status={task.status} />
          ) : null}
          {(task.labels ?? []).slice(0, 3).map((l) => (
            <TaskLabel key={l.label} label={l} />
          ))}
        </span>
        {counts.length ? <MetaCounts items={counts} className="shrink-0 max-md:hidden" /> : null}
      </div>
      {columns.map((c) => (
        <span
          key={c}
          className={cn(
            COLUMN_W[c],
            "relative z-10 flex shrink-0 items-center text-xs tabular-nums max-sm:hidden",
            selected || active ? "text-secondary" : "text-muted-foreground",
          )}
        >
          {c === "assignees" ? (
            cells?.assignees ? (
              cells.assignees(task)
            ) : task.assignees?.length === 1 && task.assignees[0] ? (
              <span
                className="flex min-w-0 items-center gap-1.5"
                title={task.assignees[0].title ?? task.assignees[0].name}
              >
                <AvatarTile size="sm" {...toAvatar(task.assignees[0])} />
                <span className="truncate">
                  {task.assignees[0].label ?? task.assignees[0].name}
                </span>
              </span>
            ) : task.assignees?.length ? (
              <AvatarStack
                size="sm"
                max={2}
                items={task.assignees.map(toAvatar)}
                title={task.assignees.map((a) => a.title ?? a.name).join(", ")}
              />
            ) : null
          ) : null}
          {c === "due" ? (
            cells?.due ? (
              cells.due(task)
            ) : task.due ? (
              <DueLabel due={task.due} overdue={task.overdue} />
            ) : null
          ) : null}
          {c === "priority" ? (
            cells?.priority ? (
              cells.priority(task)
            ) : task.priority ? (
              <PriorityMark priority={task.priority} />
            ) : null
          ) : null}
        </span>
      ))}
      {cells?.actions ? (
        <span className={cn(ACTIONS_W, "relative z-10 flex items-center justify-end gap-0.5")}>
          {cells.actions(task)}
        </span>
      ) : null}
    </li>
  );
}
